import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useSearchParams } from 'react-router-dom';
import { parsePositiveInteger } from '../parsePositiveInteger';

import type { TableQueryType } from 'types';
import type { Slicers } from 'types/services';
import { SLICER_KEYS } from 'types/services';
import {
  useGetTablesQueries,
  setTableQuery,
  updateCachedData,
  updateTableFilters,
  updateTablePagination,
  updateTableSearch,
  updateTableSort,
} from 'store/entities/tablesQuery';
import { DEFAULT_PAGE_SIZE, useDefaultPageSize } from './useDefaultPageSize';

export { DEFAULT_PAGE_SIZE };

const DEFAULT_PAGE = 1;

// Listings on one screen write pagination in the same turn, before the router
// re-renders. Remember the latest params so the next write keeps the previous ones.
// The history entry is part of the identity. A write keeps the pre-write url as the
// key, so a later visit to that same path must not reuse params from the previous entry.
let routerLocationKey: string | null = null;
let pendingSearchParams = new URLSearchParams();

const getLatestSearchParams = (visitKey: string, pathname: string, routerParams: URLSearchParams): URLSearchParams => {
  const locationKey = `${visitKey}|${pathname}?${routerParams.toString()}`;

  if (locationKey !== routerLocationKey) {
    routerLocationKey = locationKey;
    pendingSearchParams = new URLSearchParams(routerParams);
  }

  return pendingSearchParams;
};

const publishSearchParams = (next: URLSearchParams) => {
  pendingSearchParams = next;
};

type UseTableQueryControlsOptions = {
  syncPaginationWithUrl?: boolean;
  pageParam?: string;
  pageSizeParam?: string;
};

// parsePositiveInteger moved to shared utils

const sanitizeKeyForQueryParams = (key: string): string => {
  const sanitizedKey = key.replace(/[^a-zA-Z0-9]/g, '');
  return sanitizedKey || 'table';
};

export const useTableQueryControls = <T, P, CachedData = 'none'>(
  key: string,
  defaultValues: TableQueryType<T, P, CachedData>[string],
  options?: UseTableQueryControlsOptions
): {
  state: TableQueryType<T, P, CachedData>[string] | undefined;
  setPagination: (pagination: TableQueryType<T, P>[string]['pagination']) => void;
  setSort: (sort: { by: string; order: 'asc' | 'desc'; type?: string }) => void;
  setSearch: (search: string) => void;
  setFilters: (filters: {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [K in keyof P]: any;
  }) => void;
  setCachedData: (data: CachedData) => void;
  getPageSize: () => number;
} => {
  const dispatch = useDispatch();
  const tablesQueries = useGetTablesQueries();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const normalizedKey = sanitizeKeyForQueryParams(key);
  const fallbackPageParam = `${normalizedKey}Page`;
  const fallbackPageSizeParam = `${normalizedKey}PageSize`;
  const pageParam = options?.pageParam ?? `${normalizedKey}Page`;
  const pageSizeParam = options?.pageSizeParam ?? `${normalizedKey}PageSize`;
  const shouldSyncPaginationWithUrl = options?.syncPaginationWithUrl ?? true;

  const defaultPage = defaultValues.pagination.page || DEFAULT_PAGE;
  const defaultPageSize = useDefaultPageSize(defaultValues.pagination.pageSize ?? DEFAULT_PAGE_SIZE);

  const getPaginationFromUrl = useCallback(() => {
    const pageFromUrl = parsePositiveInteger(searchParams.get(pageParam)) ?? parsePositiveInteger(searchParams.get(fallbackPageParam));
    const pageSizeFromUrl = parsePositiveInteger(searchParams.get(pageSizeParam)) ?? parsePositiveInteger(searchParams.get(fallbackPageSizeParam));

    return {
      page: pageFromUrl ?? defaultPage,
      pageSize: pageSizeFromUrl ?? defaultPageSize,
    };
  }, [defaultPage, defaultPageSize, fallbackPageParam, fallbackPageSizeParam, pageParam, pageSizeParam, searchParams]);

  const latestPaginationRef = useRef<{ page: number; pageSize: number } | undefined>(undefined);
  const latestSearchRef = useRef<string>(String(defaultValues.search ?? ''));
  const pendingUrlSyncRef = useRef<{ page: number; pageSize: number } | null>(null);

  const setSearchParamsRef = useRef(setSearchParams);
  const locationRef = useRef(location);

  setSearchParamsRef.current = setSearchParams;
  locationRef.current = location;

  const updatePaginationInUrl = useCallback(
    (page: number, pageSize: number) => {
      if (!shouldSyncPaginationWithUrl) return;

      const { key: visitKey, pathname, search, state } = locationRef.current;
      const currentSearchParams = getLatestSearchParams(visitKey, pathname, new URLSearchParams(search));
      const currentPageParam = currentSearchParams.get(pageParam);
      const currentPageSizeParam = currentSearchParams.get(pageSizeParam);
      const nextPage = String(page);
      const nextPageSize = String(pageSize);

      if (currentPageParam === nextPage && currentPageSizeParam === nextPageSize) {
        pendingUrlSyncRef.current = null;
        return;
      }

      const nextSearchParams = new URLSearchParams(currentSearchParams);
      nextSearchParams.set(pageParam, nextPage);
      nextSearchParams.set(pageSizeParam, nextPageSize);
      pendingUrlSyncRef.current = { page, pageSize };
      publishSearchParams(nextSearchParams);
      // Replace so opening a record, or a listing mounting inside it, does not
      // add a history entry. Back should leave the page, not strip page params.
      setSearchParamsRef.current(nextSearchParams, { replace: true, state });
    },
    [pageParam, pageSizeParam, shouldSyncPaginationWithUrl]
  );

  useEffect(() => {
    if (!tablesQueries) return;
    if (tablesQueries[key]) return;

    const initialPagination = shouldSyncPaginationWithUrl ? getPaginationFromUrl() : defaultValues.pagination;

    dispatch(
      setTableQuery({
        [key]: {
          ...defaultValues,
          pagination: initialPagination,
        },
      })
    );
  }, [defaultValues, dispatch, getPaginationFromUrl, key, shouldSyncPaginationWithUrl, tablesQueries]);

  const state = useSelector((shape: Slicers) => {
    const filters = shape[SLICER_KEYS.TABLE_QUERY];

    if (!filters) return undefined;
    if (!filters[key]) return undefined;

    return filters[key] as TableQueryType<T, P, CachedData>[string];
  });

  const urlPagination = useMemo(() => {
    if (!shouldSyncPaginationWithUrl) return undefined;
    return getPaginationFromUrl();
  }, [getPaginationFromUrl, shouldSyncPaginationWithUrl]);

  type TableQueryState = TableQueryType<T, P, CachedData>[string];

  const fallbackState = useMemo<TableQueryState>(
    () => ({
      ...defaultValues,
      pagination: urlPagination ?? defaultValues.pagination,
    }),
    [defaultValues, urlPagination]
  );

  const syncedState: TableQueryState = useMemo(() => {
    if (!state) {
      return fallbackState;
    }

    if (!shouldSyncPaginationWithUrl || !urlPagination) {
      return state;
    }

    const currentPagination = state.pagination;
    if (currentPagination.page === urlPagination.page && currentPagination.pageSize === urlPagination.pageSize) {
      return state;
    }

    return {
      ...state,
      pagination: urlPagination,
    };
  }, [fallbackState, shouldSyncPaginationWithUrl, state, urlPagination]);

  useEffect(() => {
    latestPaginationRef.current = syncedState?.pagination;
  }, [syncedState?.pagination]);

  useEffect(() => {
    latestSearchRef.current = String(syncedState?.search ?? '');
  }, [syncedState?.search]);

  useEffect(() => {
    if (!shouldSyncPaginationWithUrl) return;
    if (!state?.pagination) return;

    const currentPagination = state.pagination;
    const paginationFromUrl = getPaginationFromUrl();
    const pendingUrlSync = pendingUrlSyncRef.current;

    if (pendingUrlSync) {
      const isCurrentStatePending = currentPagination.page === pendingUrlSync.page && currentPagination.pageSize === pendingUrlSync.pageSize;
      const isUrlPending = paginationFromUrl.page === pendingUrlSync.page && paginationFromUrl.pageSize === pendingUrlSync.pageSize;

      if (isCurrentStatePending && !isUrlPending) {
        return;
      }

      if (isUrlPending) {
        pendingUrlSyncRef.current = null;
      }
    }

    const isStateOutdatedByUrl = currentPagination.page !== paginationFromUrl.page || currentPagination.pageSize !== paginationFromUrl.pageSize;
    if (!isStateOutdatedByUrl) return;

    dispatch(
      updateTablePagination({
        key,
        pagination: paginationFromUrl,
      })
    );
  }, [dispatch, getPaginationFromUrl, key, shouldSyncPaginationWithUrl, state?.pagination]);

  const setPagination = useCallback(
    (pagination: TableQueryType<T, P>[string]['pagination']) => {
      const currentPagination = latestPaginationRef.current;
      const isSame = currentPagination?.page === pagination.page && currentPagination?.pageSize === pagination.pageSize;

      if (!isSame) {
        dispatch(updateTablePagination({ key, pagination }));
      }

      updatePaginationInUrl(pagination.page, pagination.pageSize);
    },
    [dispatch, key, updatePaginationInUrl]
  );

  const setSort = (sort: { by: string; order: 'asc' | 'desc' }) => {
    dispatch(updateTableSort({ key, sort }));
  };

  const setSearch = useCallback(
    (search: string) => {
      if (latestSearchRef.current === search) return;

      latestSearchRef.current = search;
      dispatch(updateTableSearch({ key, search }));

      const currentPageSize = latestPaginationRef.current?.pageSize ?? defaultPageSize;
      updatePaginationInUrl(DEFAULT_PAGE, currentPageSize);
    },
    [defaultPageSize, dispatch, key, updatePaginationInUrl]
  );

  const setFilters = (filters: { [key: string]: string }) => {
    dispatch(updateTableFilters({ key, filters }));

    const currentPageSize = latestPaginationRef.current?.pageSize ?? defaultPageSize;
    updatePaginationInUrl(DEFAULT_PAGE, currentPageSize);
  };

  const setCachedData = useCallback(
    (data: CachedData) => {
      dispatch(updateCachedData({ key, data }));
    },
    [dispatch, key]
  );

  const getPageSize = useCallback(() => {
    return syncedState?.pagination?.pageSize || defaultPageSize;
  }, [defaultPageSize, syncedState?.pagination?.pageSize]);

  return {
    state: syncedState,
    setPagination,
    setSort,
    setSearch,
    setFilters,
    setCachedData,
    getPageSize,
  };
};
